/**
 * The last publish attempt, shown where the editor stands.
 *
 * `functions/src/publish.ts` writes `Guide.publishState` on every attempt and
 * nothing rendered it, so a blocked or degraded publish was only ever visible
 * to someone who thought to open the Firestore console. That is a report the
 * person it concerns never sees.
 *
 * The banner leads with what a reader can load right now, because that is the
 * fact the stages actually differ on, and only then explains what happened.
 * `stale` gets the strongest treatment: the guide is published, the save
 * succeeded, and the public page is nevertheless the version from *before* the
 * edit — a state that looks exactly like success from every other part of the
 * form.
 */

import React from "react";
import { Alert, Chip, Typography } from "@firecms/ui";
import type { GateIssue, PublishState } from "@zennopay/schema";

import { describeStage, formatWhen } from "./publishState";

const CHIP_SCHEME = {
    success: "greenLighter",
    error: "redLighter",
    warning: "orangeLighter",
    info: "blueLighter"
} as const;

/** A one-word badge, for a table row or a collection column. */
export function PublishStageChip({ state }: { state: PublishState }) {
    const description = describeStage(state.stage);
    return (
        <Chip size={"small"} colorScheme={CHIP_SCHEME[description.tone]}>
            {state.stage === "stale" ? "Stale" : description.label}
        </Chip>
    );
}

function IssueRow({ issue }: { issue: GateIssue }) {
    return (
        <li className={"flex flex-col gap-1"}>
            <Typography variant={"body2"}>{issue.message}</Typography>
            <div className={"flex flex-row gap-2 items-center"}>
                {issue.field && <Chip size={"small"}>{issue.field}</Chip>}
                <Typography variant={"caption"} className={"font-mono opacity-60"}>
                    {issue.code}
                </Typography>
            </div>
        </li>
    );
}

export function PublishStateBanner({ state }: { state: PublishState }) {
    const description = describeStage(state.stage);
    const issues = (state.issues ?? []) as GateIssue[];

    return (
        <Alert color={description.tone} outerClassName={"w-full"}>
            <div className={"flex flex-col gap-2 w-full"}>

                <div className={"flex flex-row gap-2 items-center flex-wrap"}>
                    <Typography variant={"subtitle2"}>Last publish attempt</Typography>
                    <PublishStageChip state={state}/>
                </div>

                <Typography variant={"body2"}>{description.headline}</Typography>

                {/* The pipeline's own sentence, kept separate from ours: it names
                    the guide, the step, and the underlying error, none of which a
                    fixed description can know. */}
                {state.message && (
                    <Typography variant={"body2"} className={"opacity-80"}>
                        {state.message}
                    </Typography>
                )}

                <Typography variant={"caption"} className={"opacity-70"}>
                    {description.guidance}
                </Typography>

                {issues.length > 0 && (
                    <div className={"flex flex-col gap-2 pt-1"}>
                        <Typography variant={"label"}>
                            {issues.length} issue{issues.length === 1 ? "" : "s"} recorded by the gate
                        </Typography>
                        <ul className={"flex flex-col gap-2 list-none pl-0"}>
                            {issues.map((issue, index) => (
                                <IssueRow key={`${issue.code}:${issue.field}:${index}`} issue={issue}/>
                            ))}
                        </ul>
                    </div>
                )}

                <Typography variant={"caption"} className={"opacity-60"}>
                    {formatWhen(state.updatedAt)}
                    {state.actor ? ` · ${state.actor}` : ""}
                    {state.snapshotLocation ? ` · snapshot ${state.snapshotLocation}` : ""}
                </Typography>
            </div>
        </Alert>
    );
}
